import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import { FiCalendar, FiPlus, FiVideo } from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getProvider } from '../VideoConference/Providers';
import { resolveExternalMeetingId, resolveEventExternalMeetingId, normalizeExternalMeetingId } from '../VideoConference/meetingIdUtils';
import CalendarService, {
  buildAddCalendarEventPayload,
  buildUpdateCalendarEventPayload,
} from '../../services/CalendarService';
import EmailService from '../../services/EmailService';
import RecipientInput from '../Email/components/RecipientInput';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../../assets/css/Calendar.css';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

// Kept as hex (not var(--provider-zoom)/var(--provider-google)) because these
// feed alpha-tinted backgrounds (`${color}15`) below — must stay in sync with
// the --provider-zoom/--provider-google tokens in src/styles/theme.css.
const PROVIDERS = [
  { key: 'zoom', label: 'Zoom', icon: <FiVideo />, color: '#2D8CFF' },
  { key: 'google', label: 'Google Meet', icon: <FaGoogle />, color: '#16a34a' },
];

const getProviderLabel = (providerKey, fallback = '') => {
  if (!providerKey) return fallback;
  return PROVIDERS.find((p) => p.key === providerKey)?.label || fallback;
};

const getProviderColor = (providerKey, fallback = 'var(--muted-foreground)') => {
  if (!providerKey) return fallback;
  return PROVIDERS.find((p) => p.key === providerKey)?.color || fallback;
};

const resolveProviderKey = (resourceProvider, meetingProvider, formProvider, joinUrl) => {
  if (resourceProvider) return resourceProvider;
  const name = (meetingProvider || '').toLowerCase();
  if (name.includes('google')) return 'google';
  if (name.includes('zoom')) return 'zoom';
  if (formProvider) return formProvider;
  return inferProviderFromJoinUrl(joinUrl);
};

const inferProviderFromJoinUrl = (joinUrl) => {
  if (!joinUrl) return null;
  const url = joinUrl.toLowerCase();
  if (url.includes('meet.google.com')) return 'google';
  if (url.includes('zoom.us')) return 'zoom';
  return null;
};

const getExternalMeetingId = (event) => resolveEventExternalMeetingId(event);

const getEventProviderKey = (event) => {
  const meeting = event?.resource?.meeting || {};
  return resolveProviderKey(
    event?.resource?.provider,
    meeting.meetingProvider,
    null,
    event?.resource?.joinUrl,
  );
};

const eventHasVideoConference = (event) => {
  const meeting = event?.resource?.meeting || {};
  return Boolean(
    event?.resource?.joinUrl
    || meeting.isVCEnabled
    || getExternalMeetingId(event),
  );
};

const parseFormDateTime = (value) => {
  const parsed = moment(value, moment.HTML5_FMT.DATETIME_LOCAL, true);
  if (!parsed.isValid()) {
    throw new Error('Invalid start time. Please check the date and time.');
  }
  return parsed.toDate();
};

const DEFAULT_FORM = {
  title: '',
  startTime: '',
  duration: 30,
  useVideoConf: false,
  provider: 'zoom',
  guests: [],
  notes: '',
  repeat: 'none',
};

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];

const buildRepeatOptions = (startTimeValue) => {
  const parsed = startTimeValue
    ? moment(startTimeValue, moment.HTML5_FMT.DATETIME_LOCAL, true)
    : null;
  const date = parsed?.isValid() ? parsed : moment();
  const weekday = date.format('dddd');
  const nthLabel = ORDINALS[Math.ceil(date.date() / 7) - 1] || 'last';
  const monthDay = date.format('MMMM D');

  return [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: `Weekly on ${weekday}` },
    { value: 'weekday', label: 'Every weekday (Monday to Friday)' },
    { value: 'monthly', label: `Monthly on the ${nthLabel} ${weekday}` },
    { value: 'annually', label: `Annually on ${monthDay}` },
  ];
};

// Mirrors the occurrence rules used server-side (ZoomService.BuildZoomRecurrence /
// GoogleMeetService.BuildGoogleRecurrence) so the calendar grid shows the same
// occurrences that actually get created on Zoom/Google — these are display-only,
// nothing here is persisted; every occurrence still maps back to the single
// master calendar_events row for edit/delete/reschedule.
const nthWeekdayOfMonth = (year, monthIndex, weekday, nth) => {
  if (nth === -1) {
    const d = moment([year, monthIndex]).endOf('month').startOf('day');
    while (d.day() !== weekday) d.subtract(1, 'day');
    return d;
  }
  const d = moment([year, monthIndex, 1]);
  let count = 0;
  while (true) {
    if (d.day() === weekday) {
      count += 1;
      if (count === nth) return d;
    }
    d.add(1, 'day');
  }
};

const expandRecurrenceStarts = (startDate, recurrenceType) => {
  const base = moment(startDate);
  if (!recurrenceType || recurrenceType === 'none' || !base.isValid()) return [base];

  switch (recurrenceType) {
    case 'daily':
      return Array.from({ length: 30 }, (_, i) => moment(base).add(i, 'days'));

    case 'weekly':
      return Array.from({ length: 12 }, (_, i) => moment(base).add(i, 'weeks'));

    case 'weekday': {
      const starts = [];
      const cursor = moment(base);
      while (starts.length < 50) {
        if (cursor.day() >= 1 && cursor.day() <= 5) starts.push(moment(cursor));
        cursor.add(1, 'day');
      }
      return starts;
    }

    case 'monthly': {
      const nthWeek = Math.ceil(base.date() / 7);
      const monthlyWeek = nthWeek >= 5 ? -1 : nthWeek;
      const weekday = base.day();
      return Array.from({ length: 12 }, (_, i) => {
        const target = moment([base.year(), base.month() + i, 1]);
        return nthWeekdayOfMonth(target.year(), target.month(), weekday, monthlyWeek)
          .hour(base.hour())
          .minute(base.minute())
          .second(0);
      });
    }

    case 'annually':
      return Array.from({ length: 5 }, (_, i) => moment(base).add(i, 'years'));

    default:
      return [base];
  }
};

const mapApiEventToCalendarEvents = (item) => {
  const baseStart = item.startTime ? new Date(item.startTime) : new Date();
  const duration = Number(item.durationInMinutes) || 30;
  const providerName = (item.meetingProvider || '').toLowerCase();
  const providerKey = providerName.includes('google')
    ? 'google'
    : providerName.includes('zoom')
      ? 'zoom'
      : inferProviderFromJoinUrl(item.meetingUrl);
  const recurrenceType = item.recurrenceType || item.RecurrenceType || 'none';

  const occurrenceStarts = expandRecurrenceStarts(baseStart, recurrenceType);

  return occurrenceStarts.map((occStart, index) => {
    const start = occStart.toDate();
    return {
      id: index === 0 ? item.id : `${item.id}::occ${index}`,
      title: item.title || 'Untitled',
      start,
      end: new Date(start.getTime() + duration * 60000),
      resource: {
        masterId: item.id,
        isVirtualOccurrence: index > 0,
        provider: providerKey,
        joinUrl: item.meetingUrl || null,
        externalMeetingId: item.ExternalMeetingId ?? item.externalMeetingId ?? null,
        meeting: {
          topic: item.title,
          startTime: item.startTime,
          duration,
          guests: item.guestEmails || item.guests || [],
          notes: item.notes ?? '',
          isVCEnabled: Boolean(item.isVCEnabled),
          meetingProvider: item.meetingProvider || '',
          externalMeetingId: item.ExternalMeetingId ?? item.externalMeetingId ?? null,
          recurrenceType,
        },
      },
    };
  });
};

const parseCalendarList = (resp) => {
  const data = resp?.data?.data ?? resp?.data ?? [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (data && typeof data === 'object' && data.id) return [data];
  return [];
};

const getApiErrorMessage = (err, fallback) => {
  if (!err?.response && err?.message === 'Network Error') {
    return 'Unable to reach the server. Check that the API is running and CORS allows http://localhost:3000.';
  }
  return err?.response?.data?.message || err?.message || fallback;
};

const confirmSendUpdateEmail = async () => {
  const result = await Swal.fire({
    title: 'Send update email?',
    text: 'Do you want to send update event email to users?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonColor: 'var(--primary)',
    cancelButtonColor: 'var(--muted-foreground)',
  });
  return result.isConfirmed;
};

export default function CalendarPage() {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loadingEvents, setLoadingEvents] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingEvent, setEditingEvent] = useState(null);
  const [updatingEventId, setUpdatingEventId] = useState(null);
  const [showEmailBanner, setShowEmailBanner] = useState(false);

  const refreshEmailBanner = useCallback(async () => {
    try {
      const shouldShow = await EmailService.shouldShowCalendarEmailBanner();
      setShowEmailBanner(shouldShow);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleCalendarEmailError = useCallback((err) => {
    if (EmailService.isGmailSessionExpiredError(err)) {
      setShowEmailBanner(true);
      return true;
    }
    return false;
  }, []);

  const fetchConnectedProviders = useCallback(async () => {
    const connected = [];
    for (const { key } of PROVIDERS) {
      try {
        const provider = getProvider(key);
        const token = await provider.getValidAccessToken().catch(() => null);
        if (token) connected.push(key);
      } catch {
        // provider not connected — skip
      }
    }
    setConnectedProviders(connected);
  }, []);

  const fetchCalendarEvents = useCallback(async () => {
    setLoadingEvents(true);
    setLoadError(null);
    try {
      const resp = await CalendarService.getCalendarEvents();
      const list = parseCalendarList(resp);
      setEvents(list.filter((item) => item.startTime).flatMap(mapApiEventToCalendarEvents));
    } catch (err) {
      console.error(err);
      const message = getApiErrorMessage(err, 'Failed to load calendar events.');
      setLoadError(message);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    const appToken = localStorage.getItem('accessToken');
    if (!appToken) { navigate('/signin'); return; }
    refreshEmailBanner();
    fetchCalendarEvents();
    fetchConnectedProviders();
  }, [navigate, fetchCalendarEvents, fetchConnectedProviders, refreshEmailBanner]);

  useEffect(() => {
    const handleFocus = () => refreshEmailBanner();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshEmailBanner]);

  const openCreateModal = (startDate) => {
    const start = startDate
      ? moment(startDate).format('YYYY-MM-DDTHH:mm')
      : moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm');
    setEditingEvent(null);
    setForm({ ...DEFAULT_FORM, startTime: start });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setEditingEvent(null);
    setForm(DEFAULT_FORM);
  };

  const openEditModal = () => {
    if (!selectedEvent) return;
    const meeting = selectedEvent.resource?.meeting || {};
    // Recurring series are edited as a whole via the master event — always
    // target the master id/date, even if the user clicked a later occurrence.
    const masterId = selectedEvent.resource?.masterId ?? selectedEvent.id;
    setEditingEvent({ ...selectedEvent, id: masterId });
    setForm({
      title: meeting.topic || selectedEvent.title || '',
      startTime: moment(meeting.startTime || selectedEvent.start).format('YYYY-MM-DDTHH:mm'),
      duration: meeting.duration || 30,
      useVideoConf: Boolean(selectedEvent.resource?.joinUrl || meeting.isVCEnabled),
      provider: resolveProviderKey(
        selectedEvent.resource?.provider,
        meeting.meetingProvider,
        null,
        selectedEvent.resource?.joinUrl,
      ) || inferProviderFromJoinUrl(selectedEvent.resource?.joinUrl) || 'zoom',
      guests: meeting.guests || [],
      notes: meeting.notes || '',
      repeat: meeting.recurrenceType || 'none',
    });
    setShowEventModal(false);
    setShowCreateModal(true);
  };

  const handleSelectSlot = ({ start }) => {
    if (moment(start).startOf('day').isBefore(moment().startOf('day'))) return;
    openCreateModal(start);
  };
  const handleSelectEvent = (event) => { setSelectedEvent(event); setShowEventModal(true); };

  const persistEventUpdate = async ({
    calendarEventId,
    title,
    start,
    end,
    guests = [],
    notes = '',
    isVCEnabled = false,
    providerKey = null,
    joinUrl = null,
    externalMeetingId = null,
    meetingProviderFallback = '',
    sendEmail = false,
    recurrenceType = null,
  }) => {
    const durationInMinutes = Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000));
    const meetingProvider = getProviderLabel(providerKey, meetingProviderFallback);
    let meetingUrl = joinUrl;
    let meetingExternalId = normalizeExternalMeetingId(
      externalMeetingId,
      providerKey,
      joinUrl,
    );

    if (isVCEnabled && externalMeetingId && providerKey) {
      const provider = getProvider(providerKey);
      const token = await provider.getValidAccessToken().catch(() => null);
      if (!token) {
        throw new Error(
          `Connect your ${meetingProvider} account under Video Conf before rescheduling.`,
        );
      }
      const updated = await provider.updateMeeting(String(externalMeetingId), {
        topic: (title || '').trim(),
        startTime: moment(start).toISOString(),
        duration: durationInMinutes,
        guests,
        recurrenceType,
      });
      meetingUrl = updated?.joinUrl || updated?.meetingUrl || meetingUrl;
      meetingExternalId = resolveExternalMeetingId(updated, providerKey) ?? externalMeetingId;
    }

    const resp = await CalendarService.updateCalendarEvent(
      await buildUpdateCalendarEventPayload({
        id: calendarEventId,
        title,
        startTime: start,
        durationInMinutes,
        guestEmails: guests,
        notes: notes?.trim() || null,
        isVCEnabled,
        meetingProvider,
        meetingUrl,
        meetingId: meetingExternalId,
        sendEmail,
        recurrenceType,
      }),
    );

    if (resp?.data?.success === false) {
      throw new Error(resp?.data?.message || 'Failed to update calendar event.');
    }

    return { start, end, durationInMinutes, meetingUrl, meetingExternalId };
  };

  const syncEventSchedule = async (event, start, end, sendEmail = false) => {
    const meeting = event.resource?.meeting || {};
    return persistEventUpdate({
      calendarEventId: event.id,
      title: meeting.topic || event.title || '',
      start,
      end,
      guests: meeting.guests || [],
      notes: meeting.notes || '',
      isVCEnabled: Boolean(event.resource?.joinUrl || meeting.isVCEnabled),
      providerKey: getEventProviderKey(event),
      joinUrl: event.resource?.joinUrl || null,
      externalMeetingId: getExternalMeetingId(event),
      meetingProviderFallback: meeting.meetingProvider || '',
      sendEmail,
      recurrenceType: meeting.recurrenceType && meeting.recurrenceType !== 'none' ? meeting.recurrenceType : null,
    });
  };

  const applyEventScheduleLocally = (eventId, start, end, durationInMinutes) => {
    setEvents((prev) => prev.map((e) => {
      if (e.id !== eventId) return e;
      return {
        ...e,
        start,
        end,
        resource: {
          ...e.resource,
          meeting: {
            ...e.resource.meeting,
            startTime: start.toISOString(),
            duration: durationInMinutes,
          },
        },
      };
    }));
  };

  const handleEventScheduleChange = async ({ event, start, end }) => {
    if (updatingEventId) return;
    if (event.resource?.isVirtualOccurrence) {
      toast.error('This is a recurring occurrence — edit the meeting to reschedule the whole series.');
      return;
    }

    if (moment(start).startOf('day').isBefore(moment().startOf('day'))) {
      toast.error('Cannot move meetings to a past date');
      return;
    }

    const previousEvents = events;
    const sendEmail = await confirmSendUpdateEmail();
    const isRecurring = Boolean(
      event.resource?.meeting?.recurrenceType
      && event.resource.meeting.recurrenceType !== 'none',
    );

    applyEventScheduleLocally(
      event.id,
      start,
      end,
      Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000)),
    );

    try {
      setUpdatingEventId(event.id);
      const { start: newStart, end: newEnd, durationInMinutes } = await syncEventSchedule(
        event,
        start,
        end,
        sendEmail,
      );

      if (isRecurring) {
        // Occurrence dates are re-derived from the new start — refetch rather
        // than patch a single event in place.
        await fetchCalendarEvents();
      } else {
        applyEventScheduleLocally(event.id, newStart, newEnd, durationInMinutes);
      }
      if (selectedEvent?.id === event.id) {
        setSelectedEvent((prev) => (prev?.id === event.id
          ? {
              ...prev,
              start: newStart,
              end: newEnd,
              resource: {
                ...prev.resource,
                meeting: {
                  ...prev.resource.meeting,
                  startTime: newStart.toISOString(),
                  duration: durationInMinutes,
                },
              },
            }
          : prev));
      }
      toast.success('Meeting rescheduled');
    } catch (err) {
      console.error(err);
      setEvents(previousEvents);
      if (!handleCalendarEmailError(err)) {
        toast.error(getApiErrorMessage(err, 'Failed to reschedule meeting.'));
      }
    } finally {
      setUpdatingEventId(null);
    }
  };

  const createVideoMeeting = async () => {
    const providerMeta = PROVIDERS.find((p) => p.key === form.provider);
    const meetingProvider = providerMeta?.label || form.provider;

    if (!form.useVideoConf) {
      return { meetingUrl: null, meetingProvider: null, meetingId: null };
    }

    const provider = getProvider(form.provider);
    const token = await provider.getValidAccessToken().catch(() => null);
    if (!token) {
      throw new Error(
        `Connect your ${meetingProvider} account under Video Conf before scheduling.`,
      );
    }

    const meeting = await provider.createMeeting({
      topic: form.title.trim(),
      isInstant: false,
      startTime: moment(parseFormDateTime(form.startTime)).toISOString(),
      duration: Number(form.duration) || 30,
      guests: form.guests,
      recurrenceType: form.repeat !== 'none' ? form.repeat : null,
    });

    const meetingUrl = meeting?.joinUrl || meeting?.meetingUrl || '';
    if (!meetingUrl) {
      throw new Error('Video meeting created but no join URL was returned.');
    }

    const meetingId = resolveExternalMeetingId(meeting, form.provider);
    return { meetingUrl, meetingProvider, meetingId };
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.startTime) return;

    const isEditing = Boolean(editingEvent?.id);
    const sendEmail = isEditing ? await confirmSendUpdateEmail() : false;

    try {
      setSubmitting(true);

      let resp;
      if (isEditing) {
        const latestEvent = events.find((e) => e.id === editingEvent.id) || editingEvent;
        const meeting = latestEvent.resource?.meeting || {};
        const externalMeetingId = getExternalMeetingId(latestEvent);
        const joinUrl = latestEvent.resource?.joinUrl || '';
        const providerLocked = eventHasVideoConference(latestEvent);
        const providerKey = form.useVideoConf
          ? (providerLocked
              ? getEventProviderKey(latestEvent)
              : form.provider)
          : null;
        const start = parseFormDateTime(form.startTime);
        const durationInMinutes = Number(form.duration) || 30;
        const end = new Date(start.getTime() + durationInMinutes * 60000);

        if (form.useVideoConf && !externalMeetingId && !joinUrl) {
          const { meetingUrl, meetingProvider, meetingId } = await createVideoMeeting();
          resp = await CalendarService.updateCalendarEvent(
            await buildUpdateCalendarEventPayload({
              id: editingEvent.id,
              title: form.title,
              startTime: form.startTime,
              durationInMinutes: form.duration,
              guestEmails: form.guests,
              notes: form.notes.trim() || null,
              isVCEnabled: true,
              meetingProvider,
              meetingUrl,
              meetingId,
              sendEmail,
              recurrenceType: form.repeat,
            }),
          );
        } else {
          if (form.useVideoConf && joinUrl && !externalMeetingId) {
            throw new Error(
              'This meeting is missing a video conference ID and cannot be synced to Google Calendar or Zoom. Please recreate the meeting.',
            );
          }
          await persistEventUpdate({
            calendarEventId: editingEvent.id,
            title: form.title,
            start,
            end,
            guests: form.guests,
            notes: form.notes,
            isVCEnabled: form.useVideoConf,
            providerKey,
            joinUrl: form.useVideoConf ? joinUrl : null,
            externalMeetingId: form.useVideoConf ? externalMeetingId : null,
            meetingProviderFallback: getProviderLabel(
              providerKey,
              meeting.meetingProvider || '',
            ),
            sendEmail,
            recurrenceType: form.repeat,
          });
          resp = { data: { success: true } };
        }
      } else {
        const { meetingUrl, meetingProvider, meetingId } = await createVideoMeeting();
        const payload = await buildAddCalendarEventPayload({
          title: form.title,
          startTime: form.startTime,
          durationInMinutes: form.duration,
          guestEmails: form.guests,
          notes: form.notes.trim() || null,
          isVCEnabled: form.useVideoConf,
          meetingProvider: meetingProvider || '',
          meetingUrl: meetingUrl || '',
          meetingId,
          recurrenceType: form.repeat,
        });
        resp = await CalendarService.addCalendarEvent(payload);
      }

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || 'Failed to save calendar event.');
      }

      toast.success(isEditing ? 'Meeting updated!' : 'Meeting scheduled!');
      closeCreateModal();
      setSelectedEvent(null);
      await fetchCalendarEvents();
    } catch (err) {
      console.error(err);
      if (!handleCalendarEmailError(err)) {
        toast.error(getApiErrorMessage(err, 'Failed to save meeting.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?.id) return;
    const masterId = selectedEvent.resource?.masterId ?? selectedEvent.id;
    const confirmMessage = selectedEvent.resource?.meeting?.recurrenceType
      && selectedEvent.resource.meeting.recurrenceType !== 'none'
      ? 'Delete this recurring meeting? This will delete the entire series.'
      : 'Delete this calendar event?';
    if (!window.confirm(confirmMessage)) return;

    try {
      setDeleting(true);
      const resp = await CalendarService.deleteCalendarEvent(masterId);
      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || 'Failed to delete calendar event.');
      }
      toast.success('Meeting deleted');
      setShowEventModal(false);
      setSelectedEvent(null);
      await fetchCalendarEvents();
    } catch (err) {
      console.error(err);
      if (!handleCalendarEmailError(err)) {
        toast.error(getApiErrorMessage(err, 'Failed to delete meeting'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const eventStyleGetter = (event) => {
    const { provider } = event.resource;
    const isBranded = provider === 'zoom' || provider === 'google';
    const backgroundColor = getProviderColor(provider, 'var(--accent)');
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        border: 'none',
        color: isBranded ? '#fff' : 'var(--accent-foreground)',
        fontSize: '0.78rem',
        padding: '2px 6px',
        opacity: event.resource?.isVirtualOccurrence ? 0.75 : 1,
      },
    };
  };

  return (
    <div className="cal-wrapper">
      {/* Header */}
      <div className="cal-header">
        <div>
          <h5 className="cal-title page-title">
            <FiCalendar className="me-2" />
            Calendar
          </h5>
          <p className="cal-subtitle page-subtitle">Schedule and manage your meetings</p>
        </div>
        <div className="cal-header-right">
          <div className="cal-provider-status">
            {PROVIDERS.map(({ key, label, color }) => (
              <span key={key} className="cal-provider-dot" title={connectedProviders.includes(key) ? `${label} connected` : `${label} not connected`}>
                <span className="dot" style={{ backgroundColor: connectedProviders.includes(key) ? color : '#ccc' }} />
                {label}
              </span>
            ))}
          </div>
          <Button className="cal-btn-new" onClick={() => openCreateModal()}>
            <FiPlus className="me-1" />
            New Meeting
          </Button>
        </div>
      </div>

      {loadError ? (
        <div className="alert alert-warning small py-2 mb-3 mx-0">
          {loadError}
        </div>
      ) : null}

      {showEmailBanner ? (
        <div className="cal-email-banner">
          <span>
            We will not be able to send email for any save calendar events, please connect your email now.
          </span>
          <button
            type="button"
            className="cal-email-banner-connect"
            onClick={() => navigate('/email')}
          >
            Connect
          </button>
        </div>
      ) : null}

      {/* Calendar */}
      <div className="cal-container">
        {loadingEvents && <div className="cal-loading">Loading meetings…</div>}
        <DnDCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 560 }}
          view={currentView}
          date={currentDate}
          onView={setCurrentView}
          onNavigate={setCurrentDate}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={handleEventScheduleChange}
          onEventResize={handleEventScheduleChange}
          selectable
          resizable
          draggableAccessor={(event) => !updatingEventId && !event.resource?.isVirtualOccurrence}
          resizableAccessor={(event) => !updatingEventId && !event.resource?.isVirtualOccurrence}
          eventPropGetter={eventStyleGetter}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          popup
        />
      </div>

      {/* Legend */}
      <div className="cal-legend">
        {PROVIDERS.map(({ key, label, color }) => (
          <span key={key} className="cal-legend-item">
            <span className="cal-legend-dot" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
        <span className="cal-legend-item">
          <span className="cal-legend-dot" style={{ backgroundColor: 'var(--accent)', border: '1px solid var(--accent-foreground)' }} />
          No video
        </span>
      </div>

      {/* ── Create Meeting Modal ── */}
      <Modal show={showCreateModal} onHide={closeCreateModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            {editingEvent ? 'Edit Meeting' : 'Schedule Meeting'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Title */}
            <Form.Group className="mb-3">
              <Form.Label className="cal-form-label">Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Meeting title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-7">
                {/* Start Time */}
                <Form.Group className="mb-3">
                  <Form.Label className="cal-form-label">Start Time *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.startTime}
                    onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  />
                </Form.Group>
              </div>
              <div className="col-md-5">
                {/* Duration */}
                <Form.Group className="mb-3">
                  <Form.Label className="cal-form-label">Duration (min)</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.duration}
                    min={5}
                    max={480}
                    onChange={(e) => setForm((f) => ({ ...f, duration: Number(e.target.value) }))}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Guest Emails */}
            <Form.Group className="mb-3">
              <Form.Label className="cal-form-label">Add Guests</Form.Label>
              <RecipientInput
                key={editingEvent?.id ?? 'new-meeting'}
                initialEmails={form.guests}
                onChange={(emails) => setForm((f) => ({ ...f, guests: emails }))}
              />
            </Form.Group>

            {/* Repeat */}
            <Form.Group className="mb-3">
              <Form.Label className="cal-form-label">Repeat</Form.Label>
              <Form.Select
                value={form.repeat}
                onChange={(e) => setForm((f) => ({ ...f, repeat: e.target.value }))}
              >
                {buildRepeatOptions(form.startTime).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Notes */}
            <Form.Group className="mb-3">
              <Form.Label className="cal-form-label">Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Agenda, links, or any notes…"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Form.Group>

            {/* Video Conference toggle */}
            <div className="cal-video-toggle mb-2">
              <Form.Check
                type="switch"
                id="useVideoConf"
                label="Add video conference"
                checked={form.useVideoConf}
                onChange={(e) => setForm((f) => ({ ...f, useVideoConf: e.target.checked }))}
              />
            </div>

            {form.useVideoConf && (
              editingEvent && eventHasVideoConference(editingEvent) ? (
                <div className="mt-2">
                  <span className="cal-form-label d-block mb-1">Conference provider</span>
                  {form.provider ? (
                    <Badge style={{
                      backgroundColor: getProviderColor(form.provider),
                      fontSize: '0.78rem',
                    }}>
                      {getProviderLabel(form.provider)}
                    </Badge>
                  ) : (
                    <span className="text-muted small">No video conference</span>
                  )}
                  <p className="text-muted small mb-0 mt-1">
                    Conference provider cannot be changed when updating a meeting.
                  </p>
                </div>
              ) : (
                <div className="d-flex gap-2 mt-2">
                  {PROVIDERS.map(({ key, label, icon, color }) => (
                    <div
                      key={key}
                      className={`cal-provider-pill ${form.provider === key ? 'active' : ''}`}
                      style={form.provider === key ? { borderColor: color, backgroundColor: `${color}15` } : {}}
                      onClick={() => setForm((f) => ({ ...f, provider: key }))}
                    >
                      <span style={{ color: form.provider === key ? color : 'var(--muted-foreground)' }}>{icon}</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeCreateModal}>Cancel</Button>
          <Button
            size="sm"
            style={{ backgroundColor: 'var(--primary)', border: 'none' }}
            onClick={handleCreate}
            disabled={submitting || !form.title.trim() || !form.startTime}
          >
            {submitting
              ? editingEvent
                ? 'Saving…'
                : 'Scheduling…'
              : editingEvent
                ? 'Save Changes'
                : 'Schedule Meeting'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Event Detail Modal ── */}
      <Modal show={showEventModal} onHide={() => setShowEventModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Meeting Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="cal-event-detail">
              <div className="cal-detail-row">
                <span className="cal-detail-label">Title</span>
                <span className="cal-detail-value">{selectedEvent.resource.meeting.topic}</span>
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Platform</span>
                <span>
                  {selectedEvent.resource.provider ? (
                    <Badge style={{
                      backgroundColor: getProviderColor(selectedEvent.resource.provider),
                      fontSize: '0.72rem'
                    }}>
                      {selectedEvent.resource.provider === 'zoom' ? 'Zoom' : 'Google Meet'}
                    </Badge>
                  ) : (
                    <span className="cal-detail-value text-muted">No video conference</span>
                  )}
                </span>
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Start</span>
                <span className="cal-detail-value">{selectedEvent.start.toLocaleString()}</span>
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Duration</span>
                <span className="cal-detail-value">{selectedEvent.resource.meeting.duration || 30} min</span>
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Repeats</span>
                <span className="cal-detail-value">
                  {selectedEvent.resource.meeting.recurrenceType && selectedEvent.resource.meeting.recurrenceType !== 'none'
                    ? (buildRepeatOptions(
                        moment(selectedEvent.resource.meeting.startTime).format('YYYY-MM-DDTHH:mm'),
                      ).find((o) => o.value === selectedEvent.resource.meeting.recurrenceType)?.label
                        || selectedEvent.resource.meeting.recurrenceType)
                    : 'Does not repeat'}
                  {selectedEvent.resource.isVirtualOccurrence && (
                    <span className="text-muted small d-block">
                      This is one occurrence of the series — editing or deleting applies to all occurrences.
                    </span>
                  )}
                </span>
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Guests</span>
                {(selectedEvent.resource.meeting.guests?.length > 0) ? (
                  <div className="cal-guest-tags">
                    {selectedEvent.resource.meeting.guests.map((g) => (
                      <span key={g} className="cal-guest-tag">{g}</span>
                    ))}
                  </div>
                ) : (
                  <span className="cal-detail-value text-muted">No guests</span>
                )}
              </div>
              <div className="cal-detail-row">
                <span className="cal-detail-label">Notes</span>
                <span className="cal-detail-value">
                  {selectedEvent.resource.meeting.notes?.trim()
                    ? selectedEvent.resource.meeting.notes
                    : "--"}
                </span>
              </div>
              {selectedEvent.resource.joinUrl && (
                <div className="cal-detail-row">
                  <span className="cal-detail-label">Join URL</span>
                  <a href={selectedEvent.resource.joinUrl} target="_blank" rel="noreferrer" className="cal-join-link">
                    {selectedEvent.resource.joinUrl}
                  </a>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" size="sm" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
          <Button variant="secondary" size="sm" onClick={openEditModal}>
            Edit
          </Button>
          {selectedEvent?.resource?.joinUrl && (
            <Button size="sm" style={{ backgroundColor: 'var(--primary)', border: 'none' }}
              onClick={() => window.open(selectedEvent.resource.joinUrl, '_blank')}>
              Join Meeting
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
